import { message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Card, Form, FormGroup, Modal, ModalBody, Spinner } from "reactstrap";
import { AllowAccess } from "../../../components/BlockAccess";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  InvestmentRateTable,
  PaginationComponent,
  RSelect,
} from "../../../components/Component";
import { authHeader } from "../../../header";
import { useDebounce } from "../../../hooks/useDebounceSearch";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { statusOptions } from "../options_common";

export const InvestmentRateManagement = () => {
  const [modal, setModal] = useState({
    edit: false,
    add: false,
    view: false,
  });
  const [editId, setEditedId] = useState();
  const [formData, setFormData] = useState({
    currency: "",
    maximumAmount: 0,
    maximumTenor: 0,
    minimumAmount: 0,
    minimumTenor: 0,
    percentDirectDebit: 0,
    percentInterest: 0,
    product: 0,
    status: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(7);
  const [investmentRates, setInvestmentRates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadBtn, setIsLoadBtn] = useState(false);
  const [details, setDetails] = useState({});
  const [products, setProducts] = useState();
  const [singleProduct, setSingleProduct] = useState();
  const [search, setSearch] = useState("");

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const debouncedSearchTerm = useDebounce(search, 500);

  useEffect(
    () => {
      // if (debouncedSearchTerm) {
      getAllInvestmentRates();
      setCurrentPage(1);
      // }v
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  const onInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: parseFloat(value) });
  };

  const resetForm = () => {
    setFormData({
      currency: "",
      maximumAmount: 0,
      maximumTenor: 0,
      minimumAmount: 0,
      minimumTenor: 0,
      percentDirectDebit: 0,
      percentInterest: 0,
      product: 0,
      status: "",
    });
    setSingleProduct();
  };

  const onFormCancel = () => {
    setModal({ edit: false, add: false });
    resetForm();
  };

  const getAllProducts = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/trproduct`, {
        headers: authHeader(),
      });
      const { data } = await response.data;
      const resultData = data.body;
      const filteredProducts = resultData.filter((data) => data.status === "ACTIVE");

      if (resultData) {
        setProducts(filteredProducts);
        setIsLoading(false);
      }
      return { formData };
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getSingleProduct = async (id) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/trproduct/${id}`, {
        headers: authHeader(),
      });
      const { data } = await response.data;
      const resultData = data.body;
      if (resultData) {
        const tenorArr = resultData.tenors.map((tenor) => tenor.tenorDays);
        const sortedTenors = tenorArr.sort((a,b) => (a-b));
        setSingleProduct(resultData);
        setFormData({
          ...formData,
          maximumAmount: resultData.maxTransactionLimit,
          minimumAmount: resultData.minTransactionLimit,
          maximumTenor: sortedTenors[sortedTenors.length - 1],
          minimumTenor: sortedTenors[0],
        });
      }
    } catch (error) {}
  };

  const onProductChange = (e) => {
    const { value } = e;
    getSingleProduct(value);
  };

  const getAllInvestmentRates = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/trinvestment-rates?search=${search}`, {
        headers: authHeader(),
      });
      const { data } = await response.data;
      const resultData = data.body;
      if (resultData) {
        setInvestmentRates(resultData);
        setIsLoading(false);
      }
      return { formData };
    } catch (error) {
      setIsLoading(false);
      setModal({ add: false });
    }
  };

  const createInvestmentRate = async (obj) => {
    try {
      const data = {
        ...obj,
        product: singleProduct.id,
      };
      setIsLoadBtn(true);
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/auth/admin/trinvestment-rates`, data, {
        headers: authHeader(),
      });
      const formData = await response.data;
      if (formData) {
        getAllInvestmentRates();
        setIsLoadBtn(false);
        setIsLoading(false);
        message.success({
          content: "Rate Created",
          style: {
            marginTop: "20vh",
          },
        });
        resetForm();
      }
      return { formData };
    } catch (error) {
      const message = error.response;
      setIsLoadBtn(false);
      setIsLoading(false);
      message.error({
        content: message.data.message,
        style: {
          marginTop: "20vh",
        },
      });
      resetForm();
      return { message };
    }
  };

  const editInvestmentRate = async (obj) => {
    try {
      setIsLoadBtn(true);
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/trinvestment-rates/${editId}`,
        obj,
        {
          headers: authHeader(),
        }
      );
      const formData = await response.data;
      if (formData) {
        getAllInvestmentRates();
        setIsLoadBtn(false);
        setIsLoading(false);
        message.success({
          content: "Edited Successfully",
          style: {
            marginTop: "20vh",
          },
        });
        resetForm();
      }
      return { formData };
    } catch (error) {
      const message = error.response;
      setIsLoadBtn(false);
      setIsLoading(false);
      message.error({
        content: message.data.message,
        style: {
          marginTop: "20vh",
        },
      });
      resetForm();
      return { message };
    }
  };

  const onFormSubmit = () => {
    createInvestmentRate(formData);
    setModal({ edit: false });
    setSingleProduct();
  };

  const onEditSubmit = () => {
    editInvestmentRate(formData);
    setModal({ edit: false });
    setSingleProduct();
  };

  const onEditClick = (items) => {
    console.log(items)
    setModal({ edit: true });
    setFormData({
      currency: items.currency.id,
      product: items.product.id,
      status: items.status,
      backendBiAnnualInterestRate: items.backendBiAnnualInterestRate,
      backendMaturityRate: items.backendMaturityRate,
      backendMonthlyInterestRate: items.backendMonthlyInterestRate,
      backendQuarterlyInterestRate: items.backendQuarterlyInterestRate,
      backendUpfrontInterestRate: items.backendUpfrontInterestRate,
      biAnnualInterestRate: items.biAnnualInterestRate,
      maturityRate: items.maturityRate,
      maximumAmount: items.maximumAmount,
      maximumTenor: items.maximumTenor,
      minimumAmount: items.minimumAmount,
      minimumTenor: items.minimumTenor,
      monthlyInterestRate: items.monthlyInterestRate,
      percentDirectDebit: items.percentDirectDebit,
      quarterlyInterestRate: items.quarterlyInterestRate,
      upfrontInterestRate: items.upfrontInterestRate,
    });
    setSingleProduct(items);
    setEditedId(items.id);
  };

  const onViewClick = async (items) => {
    setModal({ view: true });
    setDetails(items);
  };

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = investmentRates?.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const { errors, register, handleSubmit } = useForm();

  useEffect(() => {
    getAllInvestmentRates();
    getAllProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productOptions =
    products?.map((prod) => ({
      value: prod.id,
      label: prod.productName,
    })) ?? [];

  const currencyOptions =
    singleProduct && Array.isArray(singleProduct.currency)
      ? singleProduct.currency?.map((currency) => ({
          label: currency.name,
          value: currency.id,
        }))
      : [];

  return (
    <React.Fragment>
      <Head title="Investment Rate Management" />
      <Content>
        <Block size="lg">
          <BlockHead>
            <BlockHeadContent>
              <div className="d-flex justify-content-between align-items-center">
                <BlockTitle tag="h4">Investment Rate</BlockTitle>
                <div className="d-flex align-items-center justify-center">
                  <div className="mr-2">
                    <input
                      className="form-control"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Search Investment Rate..."
                    />
                  </div>
                  <AllowAccess allowedRights={["CREATE_INVESTMENT_RATE"]}>
                    <Button color="primary" onClick={() => setModal({ add: true })}>
                      <Icon name="plus"></Icon>
                      <span>Add Rate</span>
                    </Button>
                  </AllowAccess>
                </div>
              </div>
            </BlockHeadContent>
          </BlockHead>
          <Card className="card-bordered card-preview">
            {isLoading ? (
              <Spinner
                color="primary"
                style={{
                  height: "3rem",
                  width: "3rem",
                  margin: "0 auto",
                }}
              />
            ) : (
              <InvestmentRateTable
                investRate={currentItems}
                modal={modal}
                onEditClick={onEditClick}
                handleView={onViewClick}
              />
            )}
          </Card>
          <div className="card-inner">
            {investmentRates && investmentRates.length > 0 ? (
              <PaginationComponent
                itemPerPage={itemPerPage}
                totalItems={investmentRates?.length}
                paginate={paginate}
                currentPage={currentPage}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">No investment rate found</span>
              </div>
            )}
          </div>
        </Block>

        <Modal isOpen={modal.add} toggle={() => setModal({ add: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Add Rate</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onFormSubmit)}>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Product Name</label>
                      <RSelect
                        options={productOptions}
                        onChange={(e) => {
                          setFormData({ ...formData, product: e.value });
                          onProductChange(e);
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Status</label>
                      <RSelect
                        value={statusOptions.find((d) => d.value === String(formData.tenorStatus))}
                        options={statusOptions}
                        onChange={(e) => {
                          setFormData({ ...formData, status: e.value });
                        }}
                      />
                    </FormGroup>
                  </Col>
                  {singleProduct ? (
                    <>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Minimum Amount</label>
                          <input
                            type="number"
                            name="minimumAmount"
                            value={formData.minimumAmount}
                            placeholder="0.00"
                            step="any"
                            onChange={(e) => setFormData({ ...formData, minimumAmount: parseFloat(e.target.value) })}
                            className="form-control"
                            ref={register({
                              required: "This field is required",
                            })}
                          />
                          {errors.minimumAmount && <span className="invalid">{errors.minimumAmount.message}</span>}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Maximum Amount</label>
                          <input
                            type="number"
                            name="maximumAmount"
                            value={formData.maximumAmount}
                            placeholder="0.00"
                            step="any"
                            onChange={(e) => setFormData({ ...formData, maximumAmount: parseFloat(e.target.value) })}
                            className="form-control"
                            ref={register({
                              required: "This field is required",
                            })}
                          />
                          {errors.maximumAmount && <span className="invalid">{errors.maximumAmount.message}</span>}
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Minimum Tenor</label>
                          <input
                            type="number"
                            name="minimumTenor"
                            value={formData.minimumTenor}
                            placeholder="0.00"
                            step="any"
                            onChange={(e) => setFormData({ ...formData, minimumTenor: parseFloat(e.target.value) })}
                            className="form-control"
                            ref={register({
                              required: "This field is required",
                            })}
                          />
                          {errors.minimumTenor && <span className="invalid">{errors.minimumTenor.message}</span>}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Maximum Tenor</label>
                          <input
                            type="number"
                            name="maximumTenor"
                            value={formData.maximumTenor}
                            placeholder="00"
                            onChange={(e) => setFormData({ ...formData, maximumTenor: parseFloat(e.target.value) })}
                            className="form-control"
                            ref={register({
                              required: "This field is required",
                            })}
                          />
                          {errors.maximumTenor && <span className="invalid">{errors.maximumTenor.message}</span>}
                        </FormGroup>
                      </Col>
                    </>
                  ) : null}

                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Currency</label>
                      <RSelect
                        options={currencyOptions}
                        onChange={(e) => setFormData({ ...formData, currency: parseFloat(e.value) })}
                      />
                    </FormGroup>
                  </Col>
                  {singleProduct ? (
                    <>
                      {singleProduct.properties ? (
                        <>
                          {singleProduct.properties.hasDirectDebit === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Direct Debit (%)</label>
                                <input
                                  type="number"
                                  name="percentDirectDebit"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) =>
                                    setFormData({ ...formData, percentDirectDebit: parseFloat(e.target.value) })
                                  }
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasMonthlyInterestRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Monthly Interest Rate (%)</label>
                                <input
                                  type="number"
                                  name="monthlyInterestRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasUpfrontInterestRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Upfront Interest Rate (%)</label>
                                <input
                                  type="number"
                                  name="upfrontInterestRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasQuarterlyInterestRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Quarterly Interest Rate (%)</label>
                                <input
                                  type="number"
                                  name="quarterlyInterestRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasMaturityRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Maturity Rate (%)</label>
                                <input
                                  type="number"
                                  name="maturityRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasBiAnnualInterestRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Bi-Annual Interest Rate (%)</label>
                                <input
                                  type="number"
                                  name="biAnnualInterestRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasBackendUpfrontInterestRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Backend Upfront Interest Rate (%)</label>
                                <input
                                  type="number"
                                  name="backendUpfrontInterestRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasBackendQuarterlyInterestRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Backend Quarterly Interest Rate (%)</label>
                                <input
                                  type="number"
                                  name="backendQuarterlyInterestRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasBackendMonthlyInterestRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Backend Monthly Interest Rate (%)</label>
                                <input
                                  type="number"
                                  name="backendMonthlyInterestRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasBackendMaturityRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Backend Maturity Rate (%)</label>
                                <input
                                  type="number"
                                  name="backendMaturityRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}

                          {singleProduct.properties.interestOptions.hasBackendBiAnnualInterestRate === true ? (
                            <Col md="6">
                              <FormGroup>
                                <label className="form-label">Backend BiAnnual Interest Rate (%)</label>
                                <input
                                  type="number"
                                  name="backendBiAnnualInterestRate"
                                  placeholder="0.00"
                                  step="any"
                                  onChange={(e) => onInputChange(e)}
                                  className="form-control"
                                  ref={register({
                                    required: "This field is required",
                                  })}
                                />
                                {errors.minimumAmount && (
                                  <span className="invalid">{errors.minimumAmount.message}</span>
                                )}
                              </FormGroup>
                            </Col>
                          ) : null}
                        </>
                      ) : null}
                    </>
                  ) : null}

                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        {isLoadBtn ? (
                          <Button color="primary" disabled>
                            <Spinner size="sm">Loading...</Spinner>
                            <span> Loading</span>
                          </Button>
                        ) : (
                          <Button color="primary" size="md" type="submit">
                            Submit
                          </Button>
                        )}
                      </li>
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light"
                        >
                          Cancel
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>
        <Modal isOpen={modal.edit} toggle={() => setModal({ edit: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Edit Rate</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onEditSubmit)}>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Status</label>
                      <RSelect
                        value={statusOptions.find((d) => d.value === String(formData.status))}
                        options={statusOptions}
                        onChange={(e) => setFormData({ ...formData, status: e.value })}
                      />
                    </FormGroup>
                  </Col>
                  {singleProduct ? (
                    <>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Minimum Amount</label>
                          <input
                            type="number"
                            name="minimumAmount"
                            value={formData.minimumAmount}
                            placeholder="0.00"
                            step="any"
                            onChange={(e) => {
                              setFormData({ ...formData, minimumAmount: parseFloat(e.target.value) });
                            }}
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Maximum Amount</label>
                          <input
                            type="number"
                            name="maximumAmount"
                            value={formData.maximumAmount}
                            placeholder="0.00"
                            step="any"
                            onChange={(e) => setFormData({ ...formData, maximumAmount: parseFloat(e.target.value) })}
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>
                    </>
                  ) : null}
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Minimum Tenor</label>
                      <input
                        type="number"
                        name="minimumTenor"
                        value={formData.minimumTenor}
                        placeholder="0.00"
                        step="any"
                        onChange={(e) => setFormData({ ...formData, minimumTenor: parseFloat(e.target.value) })}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Maximum Tenor</label>
                      <input
                        type="number"
                        name="maximumTenor"
                        value={formData.maximumTenor}
                        placeholder="00"
                        onChange={(e) => setFormData({ ...formData, maximumTenor: parseFloat(e.target.value) })}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                  {singleProduct ? (
                    <>
                      {singleProduct.percentDirectDebit? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Direct Debit (%)</label>
                            <input
                              type="number"
                              name="percentDirectDebit"
                              value={formData.percentDirectDebit}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) =>
                                setFormData({ ...formData, percentDirectDebit: parseFloat(e.target.value) })
                              }
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.monthlyInterestRate || singleProduct.product?.properties?.interestOptions?.hasMonthlyInterestRate === true ? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Monthly Interest Rate (%)</label>
                            <input
                              type="number"
                              name="monthlyInterestRate"
                              value={formData.monthlyInterestRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.upfrontInterestRate || singleProduct.product?.properties?.interestOptions?.hasUpfrontInterestRate === true? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Upfront Interest Rate (%)</label>
                            <input
                              type="number"
                              name="upfrontInterestRate"
                              value={formData.upfrontInterestRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.quarterlyInterestRate || singleProduct.product?.properties?.interestOptions?.hasQuarterlyInterestRate === true? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Quaterly Interest Rate (%)</label>
                            <input
                              type="number"
                              name="quaterlyInterestRate"
                              value={formData.quarterlyInterestRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.maturityRate || singleProduct.product?.properties?.interestOptions?.hasMaturityRate === true ? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Maturity Rate (%)</label>
                            <input
                              type="number"
                              name="maturityRate"
                              value={formData.maturityRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.biAnnualInterestRate || singleProduct.product?.properties?.interestOptions?.hasBiAnnualInterestRate === true? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Bi-Annual Interest Rate (%)</label>
                            <input
                              type="number"
                              name="biAnnualInterestRate"
                              value={formData.biAnnualInterestRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.backendUpfrontInterestRate || singleProduct.product?.properties?.interestOptions?.hasBackendUpfrontInterestRate === true? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Backend Upfront Interest Rate (%)</label>
                            <input
                              type="number"
                              name="backendUpfrontInterestRate"
                              value={formData.backendUpfrontInterestRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.backendQuaterlyInterestRate || singleProduct.product?.properties?.interestOptions?.hasBackendQuarterlyInterestRate === true? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Backend Quaterly Interest Rate (%)</label>
                            <input
                              type="number"
                              name="backendQuaterlyInterestRate"
                              value={formData.backendQuaterlyInterestRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.backendMonthlyInterestRate || singleProduct.product?.properties?.interestOptions?.hasBackendMonthlyInterestRate === true? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Backend Monthly Interest Rate (%)</label>
                            <input
                              type="number"
                              name="backendMonthlyInterestRate"
                              value={formData.backendMonthlyInterestRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.backendMaturityRate || singleProduct.product?.properties?.interestOptions?.hasBackendMaturityRate === true? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Backend Maturity Rate (%)</label>
                            <input
                              type="number"
                              name="backendMaturityRate"
                              value={formData.backendMaturityRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}

                      {singleProduct.backendBiAnnualInterestRate || singleProduct.product?.properties?.interestOptions?.hasBackendBiAnnualInterestRate === true? (
                        <Col md="6">
                          <FormGroup>
                            <label className="form-label">Backend BiAnnual Interest Rate (%)</label>
                            <input
                              type="number"
                              name="backendBiAnnualInterestRate"
                              value={formData.backendBiAnnualInterestRate}
                              placeholder="0.00"
                              step="any"
                              onChange={(e) => onInputChange(e)}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      ) : null}
                    </>
                  ) : null}

                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        {isLoadBtn ? (
                          <Button color="primary" disabled>
                            <Spinner size="sm">Loading...</Spinner>
                            <span> Loading</span>
                          </Button>
                        ) : (
                          <Button color="primary" size="md" type="submit">
                            Update
                          </Button>
                        )}
                      </li>
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light"
                        >
                          Cancel
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={modal.view} toggle={() => setModal({ view: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Investment Rate Details</h5>
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Product Name: </span>
                      <span>{details?.product?.productName ?? "N/A"}</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Currency: </span>
                      <span>{details?.currency?.name ?? "N/A"}</span>
                    </p>
                  </Col>
                </div>
                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Maximum Amount: </span>
                      <span>{details?.maximumAmount ?? "N/A"}</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Minimum Amount: </span>
                      <span>{details?.minimumAmount ?? "N/A"}</span>
                    </p>
                  </Col>
                </div>
                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Maximum Tenor: </span>
                      <span>{details?.maximumTenor ?? "N/A"}</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Minimum Tenor: </span>
                      <span>{details?.minimumTenor ?? "N/A"}</span>
                    </p>
                  </Col>
                </div>
                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Monthly Interest Rate: </span>
                      <span>{details?.monthlyInterestRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Quarterly Interest Rate: </span>
                      <span>{details?.quarterlyInterestRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                </div>
                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Upfront Interest Rate: </span>
                      <span>{details?.upfrontInterestRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Direct Debit: </span>
                      <span>{details?.percentDirectDebit ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                </div>

                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Maturity Rate: </span>
                      <span>{details?.maturityRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">BiAnnual Interest Rate: </span>
                      <span>{details?.biAnualIntesrestRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                </div>

                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Backed Upfront Interest Rate: </span>
                      <span>{details?.backendUpfrontInterestRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Backed Monthly Interest Rate: </span>
                      <span>{details?.backendMonthlyInterestRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                </div>

                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Backed Quarterly Interest Rate: </span>
                      <span>{details?.backendQuarterlyInterestRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Backed BiAnnual Interest Rate: </span>
                      <span>{details?.backendBiAnnualInterestRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                </div>

                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Backed Maturity Rate: </span>
                      <span>{details?.backendMaturityRate ?? "N/A"}(%)</span>
                    </p>
                  </Col>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};
